import { PrintReport, type PrintReportDetail } from '@/components/common/PrintReport';
import { PRINT_CELL_CLASS, PRINT_TABLE_CLASS } from '@/styles/printTable';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate, formatTime } from '@/utils/date';
import type { AttendanceDay, EmployeeInfo } from '../types/attendance.types';

interface EmployeeReportPrintProps {
  title: string;
  employee: EmployeeInfo | undefined;
  attendance: AttendanceDay[];
  dateFrom: string;
  dateTo: string;
}

const HEADERS = ['SL', 'Date', 'Status', 'In Time', 'Out Time', 'Working Hour', 'Remarks'];

function contactLine(employee: EmployeeInfo | undefined): string | undefined {
  if (employee === undefined) return undefined;

  const numbers = [employee.mobile, employee.contactNo].filter(Boolean);

  return numbers.length === 0 ? undefined : numbers.join(', ');
}

function locationLine(employee: EmployeeInfo | undefined): string | undefined {
  if (employee === undefined) return undefined;

  const parts = [employee.thana, employee.district, employee.division].filter(Boolean);

  return parts.length === 0 ? undefined : parts.join(', ');
}

export function EmployeeReportPrint({
  title,
  employee,
  attendance,
  dateFrom,
  dateTo,
}: EmployeeReportPrintProps) {
  const details: PrintReportDetail[] = [
    { label: 'Employee', value: employee?.fullName ?? undefined },
    { label: 'HR ID', value: employee?.hrEmpId ?? undefined },
    { label: 'Designation', value: employee?.designation ?? undefined },
    { label: 'Department', value: employee?.department ?? undefined },
    { label: 'Reporting To', value: employee?.reportingTo ?? undefined },
    { label: 'Contact', value: contactLine(employee) },
    { label: 'Address', value: employee?.address ?? undefined },
    { label: 'Location', value: locationLine(employee) },
    { label: 'From', value: formatDisplayDate(dateFrom) },
    { label: 'To', value: formatDisplayDate(dateTo) },
  ];

  return (
    <PrintReport title={title} details={details}>
      <table className={PRINT_TABLE_CLASS}>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th key={header} className={PRINT_CELL_CLASS}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {attendance.map((day, index) => (
            <tr key={day.date}>
              <td className={PRINT_CELL_CLASS}>{index + 1}</td>
              <td className={PRINT_CELL_CLASS}>{formatDisplayDate(day.date)}</td>
              <td className={PRINT_CELL_CLASS}>{day.status}</td>
              <td className={PRINT_CELL_CLASS}>{formatTime(day.inTime)}</td>
              <td className={PRINT_CELL_CLASS}>{formatTime(day.outTime)}</td>
              <td className={PRINT_CELL_CLASS}>{day.workingHour ?? EMPTY_VALUE}</td>
              <td className={PRINT_CELL_CLASS} />
            </tr>
          ))}
        </tbody>
      </table>
    </PrintReport>
  );
}
